/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Column } from "primereact/column";
import { accessAPI, TablaFiltrable, Titulo, useIsMobile, useOrganismo, deOrganismo, tituloBase } from "../../shared";
import { fechaCorta } from "../../portal/utilidades";
import { Card } from "primereact/card";
import { Tag } from "primereact/tag";
import SkeletonListado from "../SkeletonListado";

// Propuestas comerciales (cliente/propuesta) y sábana (cliente/propuesta/
// sabanas?todas=true) en un solo listado. Cada fila sabe a qué detalle ir:
// las comerciales a /propuesta/:id y las sábana a /propuestasabana/:id.
export default function Propuestas() {
  // Título de la pestaña, como en el admin
  useEffect(() => {
    document.title = "Propuestas";
    return () => (document.title = tituloBase());
  }, []);

  const organismo = useOrganismo();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [comerciales, setComerciales] = useState(null);
  const [sabanas, setSabanas] = useState(null);

  useEffect(() => {
    accessAPI("GET", "cliente/propuesta", null, setComerciales, () => setComerciales([]));
    accessAPI("GET", "cliente/propuesta/sabanas?todas=true", null, setSabanas, () => setSabanas([]));
  }, []);

  const todas = useMemo(() => {
    if (!comerciales || !sabanas) return null;
    return [
      ...comerciales.map((p) => ({
        id: `c-${p.id}`,
        tipo: "Comercial",
        nombre: p.solicitud?.nombrefantasia || "Propuesta comercial",
        detalle: p.solicitud?.certificadotipo?.nombre,
        emision: p.fechaemision,
        aceptacion: p.fechaaceptacion,
        _estado: p.fechaaceptacion ? "Aceptada" : "Pendiente de aceptación",
        aceptador: p.nombreAceptador,
        url: `/propuesta/${p.id}?volver=/propuestas`
      })),
      ...sabanas.map((s) => ({
        id: `s-${s.id}`,
        tipo: "Sábana",
        nombre: s.nombre || "Propuesta sábana",
        detalle: "Contrato sábana",
        emision: s.fechaEmision,
        aceptacion: s.fechaAceptacion,
        _estado: s.fechaAceptacion ? "Aceptada" : "Pendiente de aceptación",
        url: `/propuestasabana/${s.id}`
      }))
    ].sort((a, b) => new Date(b.emision) - new Date(a.emision));
  }, [comerciales, sabanas]);

  // En mobile, solo el ícono (el texto queda como title)
  const estado = (p) => {
    const [severidad, icono, texto] = p.aceptacion
      ? ["success", "pi pi-check", `Aceptada el ${fechaCorta(p.aceptacion)}`]
      : ["warning", "pi pi-clock", "Pendiente de aceptación"];
    return isMobile ? (
      <Tag severity={severidad} icon={icono} title={texto} />
    ) : (
      <Tag severity={severidad} icon={icono}>
        {texto}
      </Tag>
    );
  };

  const tipo = (p) => <Tag severity={p.tipo === "Sábana" ? "info" : undefined}>{p.tipo}</Tag>;

  // Hasta tener los datos, el esqueleto de la sección (como en el admin)
  if (!todas) return <SkeletonListado filtros={2} />;

  return (
    <>
      <Titulo
        // Flecha al Inicio, o a donde indique ?volver=
        urlVolver="/"
        subtitulo={`Propuestas comerciales y contratos sábana ${deOrganismo(organismo)} para tu empresa.`}
      >
        Propuestas
      </Titulo>
      <Card css={{ marginBottom: "1em" }}>
        <TablaFiltrable
          value={todas || []}
          searchFields={["nombre", "detalle", "tipo"]}
          filtros={[
            { campo: "tipo", label: "Tipo", labelPlural: "tipos", siempreVisible: true },
            { campo: "_estado", label: "Estado", labelPlural: "estados", siempreVisible: true }
          ]}
          nombreArchivo="propuestas"
          emptyMessage="No hay propuestas"
          className="p-tabla filas-clickeables"
          onRowClick={(e) => navigate(e.data.url)}
        >
          <Column header="Tipo" body={tipo} style={{ width: "8rem" }} exportValue={(p) => p.tipo} />
          <Column
            header="Propuesta"
            body={(p) => (
              <>
                <div className="p-celda-titulo">{p.nombre}</div>
                {p.detalle && <div className="p-celda-sub">{p.detalle}</div>}
              </>
            )}
            exportValue={(p) => p.nombre}
          />
          {!isMobile && <Column header="Emisión" body={(p) => fechaCorta(p.emision)} />}
          <Column
            header="Estado"
            body={(p) => (
              <>
                {estado(p)}
                {/* Quién aceptó, solo en escritorio */}
                {p.aceptador && !isMobile && (
                  <div className="p-celda-sub" css={{ marginTop: 4 }}>
                    Por {p.aceptador}
                  </div>
                )}
              </>
            )}
            exportValue={(p) => (p.aceptacion ? `Aceptada el ${fechaCorta(p.aceptacion)}` : p._estado)}
          />
        </TablaFiltrable>
      </Card>
    </>
  );
}
